import { CalendarDays, CircleCheck, Clock, Siren, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@ascleo/ui/lib/utils'
import type { PrescriptionUrgency } from '../machine'
import { PrescriptionSectionHeading } from './prescription-section-heading'

const RX_URGENCIES: ReadonlyArray<{ id: PrescriptionUrgency; icon: LucideIcon }> = [
  { id: 'soon', icon: Siren },
  { id: 'tomorrow', icon: Clock },
  { id: 'week', icon: CalendarDays },
]

/**
 * Renders prescription request urgency choices as a radio group with an icon
 * per level and a visible selected state (KO-86, FB-34). The choices and
 * their wording are unchanged pending the clinical-safety decision (KO-80).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PrescriptionUrgencySection requestUrgency={undefined} onSelectUrgency={() => undefined} />
 * ```
 */
export function PrescriptionUrgencySection({
  requestUrgency,
  onSelectUrgency,
}: {
  requestUrgency: PrescriptionUrgency | undefined
  onSelectUrgency: (urgency: PrescriptionUrgency) => void
}) {
  const { t } = useTranslation()

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <PrescriptionSectionHeading
        id="prescription-urgency-heading"
        number={3}
        title={t('self-service.prescription.medication-selection.urgency-title')}
        hint={t('self-service.prescription.medication-selection.urgency-hint')}
      />
      <div
        role="radiogroup"
        aria-labelledby="prescription-urgency-heading"
        className="grid grid-cols-1 gap-3 sm:grid-cols-3"
      >
        {RX_URGENCIES.map(({ id, icon: Icon }) => {
          const active = requestUrgency === id
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`frontdesk-prescription-urgency-${id}`}
              onClick={() => onSelectUrgency(id)}
              className={cn(
                'flex min-h-16 items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-colors outline-none focus-visible:ring-4 focus-visible:ring-ring/45',
                active
                  ? 'border-primary bg-primary-subtle text-primary-ink'
                  : 'border-border bg-card text-foreground hover:bg-foreground-soft',
              )}
            >
              <Icon aria-hidden className="size-6 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-lg font-semibold">
                  {t(`self-service.prescription.medication-selection.urgency-${id}`)}
                </span>
                <span className="block text-base text-muted-foreground">
                  {t(`self-service.prescription.medication-selection.urgency-${id}-meta`)}
                </span>
              </span>
              {active ? <CircleCheck aria-hidden className="size-6 shrink-0" /> : null}
            </button>
          )
        })}
      </div>
    </section>
  )
}
